import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, wsURL, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Settings, Send, KeyRound, DollarSign, Users, Briefcase, CalendarClock, Lock, Plus, X } from "lucide-react";

const COLS = [
  { id: "new", label: "New" },
  { id: "in_progress", label: "In Progress" },
  { id: "review", label: "Review" },
  { id: "delivered", label: "Delivered" },
];

function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(r => setA(r.data)).catch(()=>{}); }, []);
  const cards = [
    { icon: DollarSign, label: "Revenue", value: a ? inr(a.revenue) : "…", tint: "text-emerald-400" },
    { icon: Briefcase, label: "Orders", value: a?.orders_count ?? "…", tint: "text-violet-300" },
    { icon: Users, label: "Delivered", value: a?.by_status?.delivered ?? "…", tint: "text-cyan-300" },
    { icon: KeyRound, label: "In review", value: a?.by_status?.review ?? "…", tint: "text-amber-300" },
  ];
  return (
    <div className="grid md:grid-cols-4 gap-4" data-testid="admin-analytics">
      {cards.map((c, i) => (
        <div key={i} className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <span className="label-mono">{c.label}</span>
            <c.icon size={16} className={c.tint}/>
          </div>
          <div className="font-display font-black text-3xl mt-3">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function BookingsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/bookings").then(r => setItems(r.data)).catch(()=>{}); }, []);
  return (
    <div className="glass rounded-2xl p-6" data-testid="admin-bookings">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-mono">Discovery calls</div>
          <div className="font-display font-bold text-2xl mt-1">Recent bookings</div>
        </div>
        <CalendarClock size={18} className="text-white/40"/>
      </div>
      {items.length === 0 && <div className="text-white/50 text-sm">No bookings yet.</div>}
      <div className="divide-y divide-white/[0.06]">
        {items.map(b => (
          <div key={b.booking_id} className="py-3 flex items-start justify-between gap-4" data-testid={`booking-row-${b.booking_id}`}>
            <div className="min-w-0">
              <div className="font-semibold truncate">{b.name} <span className="text-white/50 text-xs font-mono ml-2">{b.email}</span></div>
              <div className="text-xs text-white/50 mt-0.5">{b.date} @ {b.time} · {b.timezone}</div>
              {b.notes && <div className="text-sm text-white/70 mt-1 line-clamp-2">{b.notes}</div>}
            </div>
            <span className="chip emerald shrink-0">{b.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SecretMessage({ m, isSelf }) {
  return (
    <div className={`flex ${isSelf ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-gradient-to-br from-violet-500/15 to-cyan-500/10 border border-violet-400/25"
        data-testid={`admin-chat-msg-cred-${m.message_id}`}>
        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{m.sender_name} · {m.sender_role}</div>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
            <KeyRound size={16} className="text-violet-200"/>
          </div>
          <div>
            <div className="font-display font-bold">Sealed vault sent</div>
            <div className="text-xs opacity-80">{m.label}</div>
            {m.url && <div className="text-[11px] font-mono text-cyan-200 mt-1">{m.url}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SecretForm({ projectId, projectDelivered, onSent }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ label:"", url:"", username:"", password:"" });

  const send = async () => {
    if (!f.label || !f.username || !f.password) return toast.error("Label, username, password are required");
    setBusy(true);
    try {
      await api.post("/chat/send-credential", { project_id: projectId, ...f });
      toast.success("Sealed vault sent");
      setF({ label:"", url:"", username:"", password:"" });
      setOpen(false);
      onSent?.();
    } catch (e) { toast.error(errMsg(e, "Send failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-t border-white/[0.06]" data-testid="admin-secret-form-wrap">
      {!open ? (
        <button onClick={()=>setOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-2.5 text-xs text-violet-200 hover:bg-violet-500/10 transition-colors"
          data-testid="admin-open-secret-form">
          <Lock size={12}/> {projectDelivered ? "Deliver credentials" : "Send sealed credentials"} <Plus size={12}/>
        </button>
      ) : (
        <div className="p-3 bg-black/30" data-testid="admin-secret-form">
          <div className="flex items-center justify-between mb-2">
            <span className="label-mono">Sealed vault</span>
            <button onClick={()=>setOpen(false)} className="text-white/50 hover:text-white" data-testid="admin-close-secret-form"><X size={14}/></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Label" value={f.label} onChange={e=>setF({...f,label:e.target.value})} data-testid="admin-secret-label"
              className="bg-transparent hairline-strong rounded-lg px-2 py-1.5 text-xs outline-none focus:border-violet-500"/>
            <input placeholder="URL (optional)" value={f.url} onChange={e=>setF({...f,url:e.target.value})} data-testid="admin-secret-url"
              className="bg-transparent hairline-strong rounded-lg px-2 py-1.5 text-xs outline-none focus:border-violet-500"/>
            <input placeholder="Username" value={f.username} onChange={e=>setF({...f,username:e.target.value})} data-testid="admin-secret-user"
              className="bg-transparent hairline-strong rounded-lg px-2 py-1.5 text-xs outline-none focus:border-violet-500"/>
            <input placeholder="Password" value={f.password} onChange={e=>setF({...f,password:e.target.value})} data-testid="admin-secret-pass"
              className="bg-transparent hairline-strong rounded-lg px-2 py-1.5 text-xs outline-none focus:border-violet-500"/>
          </div>
          <button disabled={busy} onClick={send} className="pill-btn primary text-xs w-full justify-center mt-3 py-2" data-testid="admin-secret-submit">
            <Lock size={12}/> {busy ? "Encrypting…" : "Encrypt & send to chat + email"}
          </button>
        </div>
      )}
    </div>
  );
}

function ProjectChat({ project }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  const load = () => api.get(`/chat/${project.project_id}`).then(r => setMsgs(r.data));

  useEffect(() => {
    if (!project) return;
    load();
    const token = localStorage.getItem("craftora_token") || "";
    const ws = new WebSocket(wsURL(project.project_id, token));
    ws.onmessage = e => { try { setMsgs(prev=>[...prev, JSON.parse(e.data)]); } catch(_){} };
    wsRef.current = ws;
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);
  useEffect(() => { bottomRef.current?.scrollIntoView({behavior:"smooth"}); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/chat/send", { project_id: project.project_id, content: text });
      setText("");
    } catch (e) { toast.error(errMsg(e, "Send failed")); }
  };

  return (
    <div className="glass rounded-2xl flex flex-col h-[520px]" data-testid="admin-chat">
      <div className="p-3 border-b border-white/[0.06] label-mono">Chat · {project.business_name}</div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {msgs.map(m => {
          const isSelf = m.sender_id === user.user_id;
          if (m.type === "credential") return <SecretMessage key={m.message_id} m={m} isSelf={isSelf}/>;
          return (
            <div key={m.message_id} className={`flex ${isSelf ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${isSelf ? "bg-violet-500 text-white" : "bg-white/5 hairline"}`}>
                <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</div>
                {typeof m.content === "string" ? m.content : JSON.stringify(m.content)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
      <SecretForm projectId={project.project_id} projectDelivered={project.status === "delivered"} onSent={load}/>
      <div className="p-2 border-t border-white/[0.06] flex gap-2">
        <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()}
          placeholder="Reply as team…"
          data-testid="admin-chat-input"
          className="flex-1 bg-transparent hairline-strong rounded-lg px-3 py-2 outline-none focus:border-violet-500 text-sm"/>
        <button onClick={send} className="pill-btn violet px-3" data-testid="admin-chat-send"><Send size={14}/></button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/orders");

    setOrders(data);

    setSelected(prev => {
      if (!prev) return data.length ? data[0] : null;

      const fresh = data.find(o => o.order_id === prev.order_id);

      return fresh || prev;
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const moveOrder = async (order, status) => {
    try {
      await api.patch("/orders/status", { order_id: order.order_id, status });
      toast.success(`Moved to ${status.replace('_',' ')}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Update failed")); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-2">Team console · {user?.role}</div>
          <h1 className="font-display font-black text-4xl">Command center</h1>
        </div>
        {user?.role === "super_admin" && (
          <Link to="/admin/settings" className="pill-btn ghost" data-testid="admin-settings-link">
            <Settings size={14}/>Settings
          </Link>
        )}
      </div>

      <div className="mt-8"><Analytics/></div>

      <h2 className="font-display font-bold text-2xl mt-12">Pipeline</h2>
      <div className="mt-4 grid md:grid-cols-4 gap-4" data-testid="kanban">
        {COLS.map(col => (
          <div key={col.id}
            onDragOver={e=>{e.preventDefault(); setDragOver(col.id);}}
            onDragLeave={()=>setDragOver(null)}
            onDrop={e=>{
              const orderId = e.dataTransfer.getData("orderId");
              const order = orders.find(o=>o.order_id===orderId);
              if (order && order.status !== col.id) moveOrder(order, col.id);
              setDragOver(null);
            }}
            data-testid={`kanban-col-${col.id}`}
            className={`kanban-col p-3 min-h-[240px] ${dragOver===col.id?"ring-1 ring-violet-500/40":""}`}>
            <div className="flex items-center justify-between px-2 pb-3">
              <span className="label-mono">{col.label}</span>
              <span className="text-xs text-white/40">{orders.filter(o=>o.status===col.id).length}</span>
            </div>
            <div className="space-y-2">
              {orders.filter(o=>o.status===col.id).map(o => (
                <div key={o.order_id}
                  draggable
                  onDragStart={e=>e.dataTransfer.setData("orderId", o.order_id)}
                  onClick={()=>setSelected(o)}
                  data-testid={`kanban-card-${o.order_id}`}
                  className={`rounded-lg p-3 cursor-grab active:cursor-grabbing bg-[#12121A] hairline hover:border-white/30 ${selected?.order_id===o.order_id?"ring-1 ring-violet-500/40":""}`}>
                  <div className="font-display font-bold text-sm">{o.business_name}</div>
                  <div className="text-[11px] text-white/50 font-mono mt-1">#{o.order_id.slice(-6)}</div>
                  <div className="text-xs text-white/60 mt-1">{o.package.toUpperCase()} · {inr(o.price)}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12"><BookingsList/></div>

      {selected && (
        <div className="mt-12 grid lg:grid-cols-2 gap-6" data-testid="admin-selected">
          <div className="space-y-6">
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-mono">Selected project</div>
                  <div className="font-display font-black text-2xl mt-1">{selected.business_name}</div>
                </div>
                <select value={selected.status} onChange={e=>moveOrder(selected, e.target.value)}
                  data-testid="admin-status-select"
                  className="bg-[#12121A] hairline-strong rounded-lg px-3 py-2 text-sm">
                  {COLS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-4 text-sm text-white/70">
                <div><div className="label-mono mb-1">Category</div>{selected.category}</div>
                <div><div className="label-mono mb-1">Style</div>{selected.style}</div>
                <div><div className="label-mono mb-1">Industry</div>{selected.industry}</div>
                <div><div className="label-mono mb-1">Audience</div>{selected.audience}</div>
                <div><div className="label-mono mb-1">Package</div>{selected.package?.toUpperCase()} · {inr(selected.price)}</div>
                <div><div className="label-mono mb-1">Client</div><span className="font-mono text-xs">{selected.client_id}</span></div>
                <div className="md:col-span-2"><div className="label-mono mb-1">Goals</div>{selected.goals}</div>
                <div className="md:col-span-2"><div className="label-mono mb-1">Pages</div>{selected.pages}</div>
                <div className="md:col-span-2"><div className="label-mono mb-1">Features</div>{selected.features}</div>
              </div>
            </div>
          </div>
          <ProjectChat project={selected}/>
        </div>
      )}
    </div>
  );
}
